import { useEffect, useState } from "react";
import { CalendarDays, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { fmtD } from "@/components/piloting/shared";
import { EventPubsPopup } from "@/components/graphisme/EventPublications";

const MONTHS_FR = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const fmtMonth = (mk) => { const [y, m] = (mk || "").split("-"); return m ? `${MONTHS_FR[+m - 1]} ${y}` : "À planifier"; };

/* Accueil espace client : rubrique Pilotage des contenus — événements + validation directe */
export function PilotageAccueil({ onOpenFull }) {
  const [data, setData] = useState(null);
  const [pubsFor, setPubsFor] = useState(null); // événement ouvert (page d'aperçus publications)

  const load = () => api.get("/content-piloting/portal/summary").then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); }, []);

  if (data === false) return null;
  if (data === null) {
    return <div className="flex justify-center py-6"><div className="w-5 h-5 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" /></div>;
  }

  const allHighlights = (data.months || []).flatMap((m) => m.highlights);
  const allItems = (data.months || []).flatMap((m) => m.items);
  const events = allHighlights
    .filter((h) => h.status !== "termine")
    .sort((a, b) => (a.event_date || a.month || "9999").localeCompare(b.event_date || b.month || "9999"));
  const itemsOf = (hid) => allItems.filter((i) => i.highlight_id === hid);
  const nToValidate = allItems.filter((i) => i.publication_status === "a_valider").length;

  return (
    <section className="space-y-2" data-testid="gclient-pilotage-accueil">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-lg flex items-center gap-2">
          Pilotage des contenus
          {nToValidate > 0 && (
            <span className="text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5" data-testid="pilotage-accueil-avalider-count">
              {nToValidate} à valider
            </span>
          )}
        </h2>
        <button onClick={onOpenFull} className="text-xs text-neutral-500 hover:text-neutral-900 transition-colors" data-testid="pilotage-accueil-all">Tout voir →</button>
      </div>

      <div className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100" data-testid="pilotage-accueil-events">
        {events.length === 0 && (
          <p className="text-sm text-neutral-400 p-5 text-center">Aucun événement prévu pour le moment — votre agence prépare votre planning.</p>
        )}
        {events.map((h) => {
          const its = itemsOf(h.id);
          const created = its.filter((i) => i.created).length;
          const nVal = its.filter((i) => i.publication_status === "a_valider").length;
          return (
            <button key={h.id} onClick={() => setPubsFor(h.id)} data-testid={`pilotage-accueil-event-${h.id}`}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-neutral-50 transition-colors">
              <span className="w-9 h-9 rounded-full bg-violet-50 border border-violet-100 flex items-center justify-center shrink-0">
                <CalendarDays className="w-4 h-4 text-violet-500" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold truncate">{h.name}</span>
                <span className="block text-[11px] text-neutral-400">
                  {h.event_date ? fmtD(h.event_date) : fmtMonth(h.month)} · {created}/{its.length} contenus créés
                  {h.shooting_date ? ` · shooting ${fmtD(h.shooting_date)}` : ""}
                </span>
              </span>
              {nVal > 0 && (
                <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 shrink-0" data-testid={`pilotage-accueil-event-avalider-${h.id}`}>
                  {nVal} à valider
                </span>
              )}
              <ChevronRight className="w-4 h-4 text-neutral-300 shrink-0" />
            </button>
          );
        })}
      </div>

      {pubsFor && (
        <EventPubsPopup event={allHighlights.find((h) => h.id === pubsFor)} items={itemsOf(pubsFor)}
          onClose={() => setPubsFor(null)} onChanged={load} />
      )}
    </section>
  );
}
